/**
 * AI CONTEXT
 * Purpose: one artwork thumbnail that opens a full-screen view and saves
 *   crop and rotation onto `students/{studentId}.artwork.{artworkId}`.
 *   Hover offers crop, rotate, and — when the parent passes a callback —
 *   share. Crop from the small image opens that full-screen view and its
 *   crop tools inside that same view; the view does not close and reopen.
 *   Tags on the image, including Shared and any the parent passes, share
 *   one row along the bottom.
 * Image: a small tile may use `thumbnailUrl`. A large tile and the zoomed
 *   view use `cropUrl` or `url` when those are already on the artwork. This
 *   does not call Storage `getDownloadURL`. A missing URL, or a thumbnail
 *   that fails to load, renders a placeholder on the tile. That failure
 *   does not close an open zoomed view whose full image still loads.
 *   `subscribe={false}` (Quality Check) keeps the passed `artwork` and does
 *   not open a student listener.
 * Share: manual. The share button calls `onShare` and nothing is sent on
 *   its own. `onDontShare` adds a separate don't-share button only when
 *   that callback is passed, so existing callers compile unchanged. On a
 *   piece already marked `dontShare` that button becomes Allow share (same
 *   callback, the parent toggles) and share is disabled until then.
 * Who can edit: the page already decided who can see this thumb. There is no
 *   second permission check here. `editable={false}` is for a hidden piece,
 *   which keeps Unhide as its only write.
 * Out of scope: the teacher app, the scanner app, and the debug artwork wall.
 */
import {
  ExpandOutlined,
  PictureOutlined,
  RotateRightOutlined,
  ShareAltOutlined,
  StopOutlined,
} from "@ant-design/icons"
import { App, Button, Tooltip } from "antd"
import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import styled from "styled-components"

import { studentAtoms } from "#atoms/studentAtoms"
import ArtworkCropModal, {
  type ArtworkCropResult,
} from "#components/ArtworkTiles/ArtworkCropModal"
import {
  ArtworkLightbox,
  STileActions,
  STileOpenButton,
} from "#components/ArtworkTiles/artworkTilePrimitives"
import { useArtworkRotationQueue } from "#components/ArtworkTiles/useArtworkRotationQueue"
import {
  isArtworkDontShare,
  isClassImageShared,
  normalizeArtworkRotation,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  type StudentArtwork,
  type StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import {
  artworkHasCrop,
  getCropEditorSourceUrl,
} from "#utils/artworkCropHelpers"
import {
  getStoredArtworkUrl,
  parseStoragePathFromUrl,
  RENDITION_LONG_EDGE,
  RENDITION_TIER,
  type RenditionTier,
  type StoredArtworkUrlPreference,
} from "#utils/artworkRenditionHelpers"
import {
  type ArtworkEditOverride,
  mergeArtworkEditOverride,
  revertStudentArtworkCrop,
  saveStudentArtworkCrop,
  saveStudentArtworkRotation,
  trackArtworkEdit,
} from "#utils/saveStudentArtworkImage"

/**
 * Small tiles may use the stored thumbnail. A large tile (the med tier, or
 * a thumb wider than the thumb rendition) uses the crop or the original so
 * it is not an upscaled thumbnail. The zoomed view always uses that larger
 * URL.
 */
const storedUrlPreference = (
  tier: RenditionTier | undefined,
  size: number,
  fill: boolean
): StoredArtworkUrlPreference => {
  if (tier === RENDITION_TIER.MED) return "full"
  if (!fill && size > RENDITION_LONG_EDGE[RENDITION_TIER.THUMB]) return "full"
  return "tile"
}

/** Passed as `source` so Amplitude can compare where the thumb is used. */
export const ARTWORK_EDIT_SOURCE = {
  STUDENT_PROFILE: "student-profile",
  STANDOUT_REVIEW: "standout-review",
  STANDOUT_CLASS_IMAGE: "standout-class-image",
  PORTFOLIO: "portfolio",
  QUALITY_CHECK: "quality-check",
} as const

const TILE_ACTIONS_MIN_PX = 120

const SThumb = styled.div<{
  $fill: boolean
  $size: number
  $radius: number
}>`
  position: ${(p) => (p.$fill ? "absolute" : "relative")};
  inset: ${(p) => (p.$fill ? 0 : "auto")};
  flex: 0 0 auto;
  width: ${(p) => (p.$fill ? "100%" : `${p.$size}px`)};
  height: ${(p) => (p.$fill ? "100%" : `${p.$size}px`)};
  border-radius: ${(p) => p.$radius}px;
  overflow: hidden;

  &:hover .artwork-tile-actions,
  &:focus-within .artwork-tile-actions {
    opacity: 1;
  }
`

const SPlainButton = styled.button`
  all: unset;
  display: block;
  width: 100%;
  height: 100%;
  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid #1677ff;
    outline-offset: -2px;
  }
`

const SPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #8c8c8c;
`

/**
 * One row along the bottom, clear of the crop, rotate, and share controls.
 * Shared, Hidden, and the other chips on this image sit here together.
 */
const SImageTags = styled.div<{ $flow?: boolean }>`
  position: ${({ $flow }) => ($flow ? "relative" : "absolute")};
  left: ${({ $flow }) => ($flow ? "auto" : "4px")};
  right: ${({ $flow }) => ($flow ? "auto" : "4px")};
  bottom: ${({ $flow }) => ($flow ? "auto" : "4px")};
  width: ${({ $flow }) => ($flow ? "100%" : "auto")};
  box-sizing: border-box;
  padding: ${({ $flow }) => ($flow ? "0 4px 4px" : "0")};
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 2px;
  pointer-events: none;
`

/** Hover actions sit above the name chips so the chips cannot cover them. */
const SBottomStack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
`

const SBottomActions = styled.div`
  opacity: 0;
  pointer-events: none;
  padding: 8px 8px 4px;
  transition: opacity 0.15s ease;
`

const SImageTag = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 16px;
  color: #fff;
  background: rgba(0, 0, 0, 0.65);
`

const ArtworkThumbPlaceholder = () => (
  <SPlaceholder data-testid="artwork-thumb-placeholder" aria-hidden>
    <PictureOutlined style={{ fontSize: 28 }} />
  </SPlaceholder>
)

export interface ArtworkEditableThumbProps {
  studentId: string
  artworkId: string
  /** Amplitude property. Use `ARTWORK_EDIT_SOURCE` when the surface is one of those. */
  source: string
  /**
   * The piece the parent already has. Used until the student subscription
   * includes this id, then the subscription wins. Ignored as a live source
   * when `subscribe` is false — Quality Check passes the snapshot entry,
   * including crops and shares written this visit.
   */
  artwork?: StudentArtwork
  /** Shown when neither the subscription nor `artwork` has a stored URL. */
  previewUrl?: string
  previewRotation?: number
  size?: number
  /** Fill a positioned parent (profile and other square tiles). */
  fill?: boolean
  objectFit?: "cover" | "contain"
  borderRadius?: number
  alt?: string
  caption?: string
  /** Accessible name of the open button. Defaults to `View {caption}`. */
  viewLabel?: string
  testId?: string
  imageTestId?: string
  /**
   * Which stored URL fits this tile. `med` is a large tile and uses
   * `cropUrl` or `url`. `thumb` (and a small untagged tile) may use
   * `thumbnailUrl`. This does not probe Storage.
   */
  tier?: RenditionTier
  /**
   * Zoom stays available. Rotate and crop hide when this is false (a hidden
   * piece on the profile, where Unhide is the only write).
   */
  editable?: boolean
  /**
   * Hover buttons for crop and rotate. Defaults on for filled tiles and for
   * thumbs at least 120px. Share and don't-share follow their callbacks
   * instead, so a parent can offer them without turning crop and rotate on.
   */
  showTileActions?: boolean
  /**
   * False when a parent already uses the click, such as the portfolio step
   * strip. The image still shows the live crop and rotation. Zoom stays on
   * the large thumb for that step.
   */
  openOnClick?: boolean
  /**
   * False on a one-shot surface (Quality Check). The thumb then does not
   * subscribe to the student, and `artwork` is what it shows.
   */
  subscribe?: boolean
  /** False when a parent shell already carries `data-artwork-id`. */
  exposeArtworkId?: boolean
  /**
   * Hide rotate even when the thumb is editable. Quality Check passes false
   * when the page did not provide a save handler.
   */
  rotateEnabled?: boolean
  /**
   * Angle to display when `onRotate` is set. The parent owns the queue, so
   * the thumb does not keep a second pending angle.
   */
  shownRotation?: number
  animateShownRotation?: boolean
  /** Replaces the thumb's own rotation queue. Called on each rotate click. */
  onRotate?: () => void
  /**
   * Hide crop even when the original path can be parsed. Quality Check
   * passes false unless it owns both the save and the revert.
   */
  cropEnabled?: boolean
  /** Replaces the thumb's own crop write. The parent uploads and records it. */
  onCropSave?: (result: ArtworkCropResult) => Promise<void>
  /** Replaces the thumb's own revert. */
  onCropRevert?: () => Promise<void>
  /**
   * Manual family share. Omitted: no share button. Nothing is sent until
   * the button is clicked.
   */
  onShare?: () => void | Promise<void>
  sharePending?: boolean
  /**
   * Separate from share. The button renders only when this is passed, so
   * standout review can adopt it later without a compile change now.
   */
  onDontShare?: () => void | Promise<void>
  dontSharePending?: boolean
  /**
   * A displayed URL failed. The thumb already shows a placeholder. A parent
   * can still hide the piece. A broken thumbnail does not call this while a
   * different full image can still load. The zoomed view calls it with the
   * full image's URL when that image fails.
   */
  onImageError?: (src: string) => void
  /**
   * The "Shared" / "Don't share" chips. Quality Check uses the border and
   * the hover icons instead, so it turns these off.
   */
  showDecisionTags?: boolean
  /**
   * Extra chips on the image (version count, judging label, and the like).
   * Drawn with the shared tag along the bottom, on every surface that uses
   * this thumb.
   */
  imageTags?: ReactNode
  /**
   * Shown on hover, directly above `imageTags`. Quality Check uses this for
   * the share choice so a competition name cannot cover the buttons.
   */
  bottomActions?: ReactNode
  extraTileActions?: ReactNode
  extraLightboxActions?: ReactNode
}

const ArtworkEditableThumb = ({
  studentId,
  artworkId,
  source,
  artwork,
  previewUrl,
  previewRotation = 0,
  size = 88,
  fill = false,
  objectFit = "cover",
  borderRadius = 6,
  alt,
  caption,
  viewLabel,
  testId,
  imageTestId,
  tier,
  editable = true,
  showTileActions,
  openOnClick = true,
  subscribe = true,
  exposeArtworkId = true,
  rotateEnabled = true,
  shownRotation,
  animateShownRotation = false,
  onRotate,
  cropEnabled = true,
  onCropSave,
  onCropRevert,
  onShare,
  sharePending = false,
  onDontShare,
  dontSharePending = false,
  onImageError,
  showDecisionTags = true,
  imageTags,
  bottomActions,
  extraTileActions,
  extraLightboxActions,
}: ArtworkEditableThumbProps) => {
  const { message } = App.useApp()
  const theme = useThemeContext()
  const { currentUserId } = useUserContext()
  const student = useOneDoc<StudentConstructor>(
    studentAtoms,
    DOC_TYPES.STUDENTS,
    subscribe ? studentId : null
  )
  const subscribed = subscribe ? student?.artwork?.[artworkId] : undefined
  const [override, setOverride] = useState<ArtworkEditOverride | null>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [cropToolsOpen, setCropToolsOpen] = useState(false)
  // The angle the crop editor opened on. Frozen for the life of the editor:
  // the live display angle includes a pending turn, and settleAfterSave drops
  // that pending once the write resolves, before the student listener echoes
  // the new value. The editor uses this number as its Reset and dirty
  // baseline, so a live value would fall back to the old angle.
  const [cropRotation, setCropRotation] = useState(0)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const base = subscribed ?? artwork
  const shown = useMemo(
    () => (base ? mergeArtworkEditOverride(base, override) : null),
    [base, override]
  )

  useEffect(() => {
    if (!override || !subscribed) return
    const cropMatches =
      override.cropUrl === null
        ? !subscribed.cropUrl
        : override.cropUrl === undefined ||
          subscribed.cropUrl === override.cropUrl
    const thumbMatches =
      override.thumbnailUrl === null
        ? !subscribed.thumbnailUrl
        : override.thumbnailUrl === undefined ||
          subscribed.thumbnailUrl === override.thumbnailUrl
    const rotationMatches =
      override.rotation === undefined ||
      (subscribed.rotation ?? 0) === override.rotation
    if (cropMatches && thumbMatches && rotationMatches) setOverride(null)
  }, [override, subscribed])

  const tileSrc =
    (shown
      ? getStoredArtworkUrl(shown, storedUrlPreference(tier, size, fill))
      : "") ||
    previewUrl ||
    ""
  const fullSrc =
    (shown ? getStoredArtworkUrl(shown, "full") : "") || previewUrl || tileSrc

  const artworkMap = useMemo(
    () => student?.artwork ?? (shown ? { [shown.id]: shown } : undefined),
    [shown, student?.artwork]
  )

  const track = useCallback(
    (action: "zoom" | "rotate" | "crop" | "revert") => {
      trackArtworkEdit({ source, action, studentId, artworkId })
    },
    [artworkId, source, studentId]
  )

  const handleRotationSave = useCallback(
    async (rotationTargets: Record<string, number>) => {
      let saved = false
      try {
        saved = await saveStudentArtworkRotation(
          studentId as Uuid,
          artworkMap,
          rotationTargets
        )
      } catch {
        void message.error("Could not save the rotation. Try again.")
        throw new Error("Could not save the rotation")
      }
      if (!saved) return
      // A crop (or revert) parks the angle on the local override until the
      // student listener echoes. A later rotate has to move that same field,
      // or the queue settling drops the pending angle and the thumb snaps
      // back — and the echo never matches, so the override never leaves.
      const savedRotation = rotationTargets[artworkId]
      if (savedRotation !== undefined) {
        const normalized = normalizeArtworkRotation(savedRotation)
        setOverride((current) => {
          if (!current || current.rotation === undefined) return current
          if (current.rotation === normalized) return current
          return { ...current, rotation: normalized }
        })
      }
      for (const id of Object.keys(rotationTargets)) {
        trackArtworkEdit({
          source,
          action: "rotate",
          studentId,
          artworkId: id,
        })
      }
    },
    [artworkId, artworkMap, message, source, studentId]
  )

  const {
    pendingRotations,
    rotate,
    yieldToEditor,
    clearPending,
    restorePending,
  } = useArtworkRotationQueue({
    // `editable` only hides the buttons. A rotate that is already queued
    // still has to write: clearing onSave makes the debounced flush a no-op,
    // which is what happened when a piece was hidden during that second.
    onSave: onRotate ? undefined : handleRotationSave,
    settleAfterSave: true,
  })

  const savedRotation = shown?.rotation ?? previewRotation
  const usingExternalRotation = onRotate !== undefined
  const rotation = usingExternalRotation
    ? (shownRotation ?? 0)
    : (pendingRotations[artworkId] ?? savedRotation)
  const animateRotation = usingExternalRotation
    ? animateShownRotation
    : artworkId in pendingRotations
  const label = caption?.trim() || shown?.filename || "Student artwork"
  const openLabel = viewLabel ?? `View ${label}`
  const canEdit = editable && !!shown
  const originalPath = shown
    ? parseStoragePathFromUrl(getCropEditorSourceUrl(shown))
    : null
  const tileActions = showTileActions ?? (fill || size >= TILE_ACTIONS_MIN_PX)
  const showRotate = rotateEnabled && canEdit
  const showCrop = cropEnabled && canEdit && !!originalPath
  const shared = isClassImageShared(shown)
  const dontShare = isArtworkDontShare(shown)
  const hasActions =
    (showRotate && tileActions) ||
    (showCrop && tileActions) ||
    !!onShare ||
    !!onDontShare ||
    !!extraTileActions

  const openLightbox = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      setCropToolsOpen(false)
      setLightboxOpen(true)
      track("zoom")
    },
    [track]
  )

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false)
    setCropToolsOpen(false)
    // A failed full-size URL can hide the tile once the view is closed.
    // Closing is the user's action; the error itself must not do it.
    if (tileSrc && failedSrc === tileSrc && (!fullSrc || fullSrc === tileSrc)) {
      onImageError?.(tileSrc)
    }
  }, [failedSrc, fullSrc, onImageError, tileSrc])

  const handleRotate = useCallback(() => {
    if (onRotate) {
      onRotate()
      return
    }
    if (!shown) return
    rotate(shown.id, shown.rotation ?? 0)
  }, [onRotate, rotate, shown])

  const openCrop = useCallback(() => {
    setCropRotation(rotation)
    setCropToolsOpen(true)
    setLightboxOpen(true)
  }, [rotation])

  const closeCropTools = useCallback(() => setCropToolsOpen(false), [])

  const handleCropSave = useCallback(
    async (result: ArtworkCropResult) => {
      if (onCropSave) {
        await onCropSave(result)
        return
      }
      if (!shown) return
      invariant(currentUserId != null, "Current user id is not defined!")
      const pending = pendingRotations[shown.id]
      await yieldToEditor(shown.id)
      try {
        const crop = await saveStudentArtworkCrop({
          studentId: studentId as Uuid,
          artworkMap,
          artwork: shown,
          result,
          userId: currentUserId,
        })
        setOverride({
          ...(crop ?? {}),
          rotation: result.rotation,
        })
        clearPending(shown.id)
        track(crop ? "crop" : "rotate")
      } catch (error) {
        if (pending !== undefined) restorePending(shown.id, pending)
        throw error
      }
    },
    [
      artworkMap,
      clearPending,
      currentUserId,
      onCropSave,
      pendingRotations,
      restorePending,
      shown,
      studentId,
      track,
      yieldToEditor,
    ]
  )

  const handleCropRevert = useCallback(async () => {
    if (onCropRevert) {
      await onCropRevert()
      return
    }
    if (!shown) return
    const paths = await revertStudentArtworkCrop(studentId as Uuid, shown)
    if (!paths) return
    const cleared: ArtworkEditOverride = { rotation: shown.rotation }
    for (const path of paths) {
      const field = path.split(".").pop()
      if (field === "cropUrl") cleared.cropUrl = null
      if (field === "cropPath") cleared.cropPath = null
      if (field === "thumbnailUrl") cleared.thumbnailUrl = null
      if (field === "thumbnailPath") cleared.thumbnailPath = null
    }
    setOverride(cleared)
    track("revert")
  }, [onCropRevert, shown, studentId, track])

  const handleImageError = useCallback(() => {
    setFailedSrc(tileSrc)
    // A different full image can still be open. Leave this thumb mounted and
    // tell Quality Check only when the URL the tile is showing is also the
    // zoomed image, and that view is not open.
    if (lightboxOpen) return
    if (fullSrc && fullSrc !== tileSrc) return
    if (tileSrc) onImageError?.(tileSrc)
  }, [fullSrc, lightboxOpen, onImageError, tileSrc])

  const tileBroken = Boolean(tileSrc) && failedSrc === tileSrc

  const artworkIdAttr = exposeArtworkId ? artworkId : undefined

  const rotateButton = (testIdSuffix: "tile" | "lightbox") => (
    <Tooltip title="Rotate 90°">
      <Button
        size={testIdSuffix === "tile" ? "small" : "middle"}
        icon={<RotateRightOutlined />}
        onClick={handleRotate}
        aria-label={`Rotate ${label}`}
        data-testid={`artwork-${testIdSuffix}-rotate`}
      />
    </Tooltip>
  )

  const cropButton = (testIdSuffix: "tile" | "lightbox") =>
    showCrop ? (
      <Tooltip title="Crop to the artwork">
        <Button
          size={testIdSuffix === "tile" ? "small" : "middle"}
          icon={<ExpandOutlined />}
          onClick={openCrop}
          aria-label={`Crop ${label}`}
          data-testid={`artwork-${testIdSuffix}-crop`}
          data-cropped={shown && artworkHasCrop(shown) ? "true" : "false"}
        />
      </Tooltip>
    ) : null

  const shareButton = (testIdSuffix: "tile" | "lightbox") =>
    onShare ? (
      <Tooltip
        title={
          dontShare
            ? "Don't share is on. Allow share first."
            : shared
              ? "Unshare with family"
              : "Share with family"
        }
      >
        <Button
          size={testIdSuffix === "tile" ? "small" : "middle"}
          icon={<ShareAltOutlined />}
          onClick={() => {
            void onShare()
          }}
          loading={sharePending}
          disabled={dontShare}
          aria-label={shared ? `Unshare ${label}` : `Share ${label}`}
          data-testid={`artwork-${testIdSuffix}-share`}
          data-shared={shared ? "true" : "false"}
          style={
            shared
              ? { color: theme.eric.green, borderColor: theme.eric.green }
              : undefined
          }
        />
      </Tooltip>
    ) : null

  const dontShareButton = (testIdSuffix: "tile" | "lightbox") =>
    onDontShare ? (
      <Tooltip
        title={
          dontShare
            ? shown?.dontShareReason?.trim()
              ? `Allow share. Not shared: ${shown.dontShareReason.trim()}`
              : "Allow share"
            : "Don't share"
        }
      >
        <Button
          size={testIdSuffix === "tile" ? "small" : "middle"}
          icon={<StopOutlined />}
          onClick={() => {
            void onDontShare()
          }}
          loading={dontSharePending}
          aria-label={
            dontShare ? `Allow share ${label}` : `Don't share ${label}`
          }
          data-testid={`artwork-${testIdSuffix}-dont-share`}
          data-dont-share={dontShare ? "true" : "false"}
          style={
            dontShare
              ? { color: theme.eric.red, borderColor: theme.eric.red }
              : undefined
          }
        />
      </Tooltip>
    ) : null

  const OpenButton = fill ? STileOpenButton : SPlainButton
  const image =
    tileSrc && !tileBroken ? (
      <img
        src={tileSrc}
        alt={alt ?? label}
        data-testid={imageTestId}
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={handleImageError}
        style={{
          width: "100%",
          height: "100%",
          objectFit,
          display: "block",
          transform: rotation ? `rotate(${rotation}deg)` : undefined,
          transition: animateRotation ? "transform 0.2s ease" : undefined,
        }}
      />
    ) : (
      <ArtworkThumbPlaceholder />
    )

  const viewActions =
    showRotate || showCrop || onShare || onDontShare || extraLightboxActions ? (
      <>
        {showCrop ? cropButton("lightbox") : null}
        {showRotate ? rotateButton("lightbox") : null}
        {shareButton("lightbox")}
        {dontShareButton("lightbox")}
        {extraLightboxActions}
      </>
    ) : undefined

  return (
    <>
      <SThumb $fill={fill} $size={size} $radius={borderRadius}>
        {openOnClick ? (
          <OpenButton
            type="button"
            onClick={openLightbox}
            aria-label={openLabel}
            title={label}
            data-testid={testId}
            data-artwork-id={artworkIdAttr}
            data-rotation={rotation}
          >
            {image}
          </OpenButton>
        ) : (
          <div
            data-testid={testId}
            data-artwork-id={artworkIdAttr}
            data-rotation={rotation}
            style={{ width: "100%", height: "100%" }}
          >
            {image}
          </div>
        )}
        {hasActions ? (
          <STileActions
            className="artwork-tile-actions"
            onClick={(event) => event.stopPropagation()}
          >
            {showCrop && tileActions ? cropButton("tile") : null}
            {showRotate && tileActions ? rotateButton("tile") : null}
            {shareButton("tile")}
            {dontShareButton("tile")}
            {extraTileActions}
          </STileActions>
        ) : null}
        {(showDecisionTags && (shared || dontShare)) ||
        imageTags ||
        bottomActions ? (
          <SBottomStack>
            {bottomActions ? (
              <SBottomActions
                className="artwork-tile-actions"
                onClick={(event) => event.stopPropagation()}
              >
                {bottomActions}
              </SBottomActions>
            ) : null}
            {(showDecisionTags && (shared || dontShare)) || imageTags ? (
              <SImageTags
                $flow={!!bottomActions}
                data-testid="artwork-image-tags"
                data-edge="bottom"
              >
                {imageTags}
                {showDecisionTags && shared ? (
                  <SImageTag data-testid="artwork-tile-shared">
                    Shared
                  </SImageTag>
                ) : null}
                {showDecisionTags && dontShare ? (
                  <SImageTag data-testid="artwork-tile-dont-share-tag">
                    Don't share
                  </SImageTag>
                ) : null}
              </SImageTags>
            ) : null}
          </SBottomStack>
        ) : null}
      </SThumb>
      <ArtworkLightbox
        open={lightboxOpen}
        artworkId={artworkId}
        src={failedSrc && failedSrc === fullSrc ? "" : fullSrc}
        caption={label}
        rotation={rotation}
        animateRotation={animateRotation}
        onClose={closeLightbox}
        actions={cropToolsOpen ? undefined : viewActions}
        empty={<ArtworkThumbPlaceholder />}
        onImageError={onImageError}
        editor={
          cropToolsOpen && shown && originalPath ? (
            <ArtworkCropModal
              open
              embedded
              originalPath={originalPath}
              rotation={cropRotation}
              caption={label}
              hasCrop={artworkHasCrop(shown)}
              onClose={closeCropTools}
              onSave={handleCropSave}
              onRevert={handleCropRevert}
            />
          ) : undefined
        }
      />
    </>
  )
}

export default ArtworkEditableThumb
